import React, { useState } from 'react';
import { Amplify } from 'aws-amplify';
import { Authenticator } from '@aws-amplify/ui-react';
import '@aws-amplify/ui-react/styles.css';
import {
  Bath,
  Utensils,
  Droplets,
  UserCircle,
  LogOut,
  ChevronDown,
  Home,
  BookOpen,
  Activity,
  PlusCircle
} from 'lucide-react';

import { amplifyConfig } from './config';
import { BathroomForm } from './components/BathroomForm';
import { MealForm } from './components/MealForm';
import { WeightForm } from './components/WeightForm';
import { BloodPressureForm } from './components/BloodPressureForm';
import { Timeline } from './components/Timeline';
import { DailySummary } from './components/DailySummary';
import { ProfilePage } from './components/ProfilePage';
import { WaterForm } from './components/WaterForm';
import { Dashboard } from './components/Dashboard';
import { HomePage } from './components/HomePage';
import { OperationalDashboard } from './components/OperationalDashboard';
import { KnowledgePage } from './components/KnowledgePage';
import { MoodPrompt } from './components/MoodPrompt';
import { OtherInputsMenu } from './components/OtherInputsMenu';
import { HealthAssistantPage } from './components/HealthAssistantPage';
import { UserProvider, useUser } from './contexts/UserContext';
import './App.css';

// Configure Amplify
Amplify.configure(amplifyConfig);

type View = 'home' | 'bathroom' | 'meal' | 'weight' | 'water' | 'blood_pressure' | 'timeline' | 'profile' | 'stats' | 'journal' | 'dashboard' | 'knowledge' | 'other_inputs' | 'assistant';

// Wrapper component to access UserContext for OtherInputsMenu
const OtherInputsMenuWrapper: React.FC<{ onSuccess: () => void; onClose: () => void }> = ({ onSuccess, onClose }) => {
  const { profile } = useUser();
  return (
    <OtherInputsMenu
      isOpen={true}
      onClose={onClose}
      onSubmit={onSuccess}
      profile={profile}
    />
  );
};

function App() {
  const [currentView, setCurrentView] = useState<View>('home');
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [highlightTraceId, setHighlightTraceId] = useState<string | null>(null);
  const [notification, setNotification] = useState<{
    message: string;
    type: 'success' | 'error' | 'warning';
  } | null>(null);

  const [showMoodPrompt, setShowMoodPrompt] = useState(false);
  const [lastLoggedTraceId, setLastLoggedTraceId] = useState<string | undefined>(undefined);
  const [lastLoggedType, setLastLoggedType] = useState<string | undefined>(undefined);

  const showNotification = (message: string, type: 'success' | 'error' | 'warning' = 'success') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleSuccess = (traceId: string, warnings?: string[]) => {
    showNotification(`Event logged! Trace ID: ${traceId}`, 'success');
    if (warnings && warnings.length > 0) {
      setTimeout(() => {
        warnings.forEach(warning => showNotification(warning, 'warning'));
      }, 500);
    }
    setRefreshTrigger(prev => prev + 1);

    // Check if we should show mood prompt
    const eligibleTypes = ['meal', 'bathroom', 'water'];
    // We need to know the type... but handleSuccess only gets traceId. 
    // Optimization: We can infer or pass type. For now, let's assume if this is called from these forms, we might want to prompt.
    // Actually, let's track the current view as a proxy for type roughly, or just check cooldown.

    // Cooldown check (4 hours = 4 * 60 * 60 * 1000 = 14400000 ms)
    const COOLDOWN_MS = 14400000;
    const lastMoodTime = parseInt(localStorage.getItem('lastMoodTimestamp') || '0');
    const timeSinceLastMood = Date.now() - lastMoodTime;

    if (eligibleTypes.includes(currentView) && timeSinceLastMood > COOLDOWN_MS) {
      setLastLoggedTraceId(traceId);
      setLastLoggedType(currentView);
      setTimeout(() => setShowMoodPrompt(true), 500); // Small delay for UX
    }
  };

  const handleViewTrace = (traceId: string) => {
    setHighlightTraceId(traceId);
    setCurrentView('timeline');
    // Clear highlight after some time to allow future highlighting
    setTimeout(() => setHighlightTraceId(null), 10000);
  };

  const handleError = (error: string) => {
    showNotification(`Error: ${error}`, 'error');
  };

  // ... (skipping some lines) ...

  {
    currentView === 'meal' && (
      <MealForm onSuccess={handleSuccess} onError={handleError} onViewTrace={handleViewTrace} />
    )
  }
  // ...
  {
    currentView === 'timeline' && (
      <div className="timeline-view">
        <DailySummary refreshTrigger={refreshTrigger} />
        <Timeline refreshTrigger={refreshTrigger} highlightTraceId={highlightTraceId} />
      </div>
    )
  }

  const handleSignOut = (signOut?: () => void) => {
    if (signOut) signOut();
    setIsUserMenuOpen(false);
  };

  return (
    <Authenticator>
      {({ signOut, user }) => (
        <UserProvider>
          <div className="App">
            <header className="app-header">
              <h3>Health Tracker</h3>

              <nav className="view-nav">
                <button
                  className={currentView === 'home' ? 'active' : ''}
                  onClick={() => setCurrentView('home')}
                >
                  <Home />
                  <span>Home</span>
                </button>
                <button
                  className={currentView === 'bathroom' ? 'active' : ''}
                  onClick={() => setCurrentView('bathroom')}
                >
                  <Bath />
                  <span>Bathroom</span>
                </button>
                <button
                  className={currentView === 'meal' ? 'active' : ''}
                  onClick={() => setCurrentView('meal')}
                >
                  <Utensils />
                  <span>Meal</span>
                </button>
                <button
                  className={currentView === 'other_inputs' ? 'active' : ''}
                  onClick={() => setCurrentView('other_inputs')}
                >
                  <PlusCircle />
                  <span>Other Inputs</span>
                </button>
              </nav>

              <div className="user-controls">
                <button
                  className="user-menu-button"
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                >
                  {user?.signInDetails?.loginId || 'User'}
                  <ChevronDown size={16} />
                </button>

                {isUserMenuOpen && (
                  <div className="user-dropdown">
                    <button
                      onClick={() => {
                        setCurrentView('profile');
                        setIsUserMenuOpen(false);
                      }}
                      className="dropdown-item"
                    >
                      <UserCircle size={16} />
                      Profile
                    </button>
                    <button
                      onClick={() => {
                        setCurrentView('assistant');
                        setIsUserMenuOpen(false);
                      }}
                      className="dropdown-item"
                    >
                      💬 Health Assistant
                    </button>
                    <button
                      onClick={() => {
                        setCurrentView('dashboard');
                        setIsUserMenuOpen(false);
                      }}
                      className="dropdown-item"
                    >
                      <Activity size={16} />
                      Operational Dashboard
                    </button>
                    <button
                      onClick={() => {
                        setCurrentView('knowledge');
                        setIsUserMenuOpen(false);
                      }}
                      className="dropdown-item"
                    >
                      <BookOpen size={16} />
                      Knowledge Base
                    </button>
                    <button
                      onClick={() => handleSignOut(signOut)}
                      className="dropdown-item"
                    >
                      <LogOut size={16} />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </header>

            {notification && (
              <div className={`notification ${notification.type}`}>
                {notification.message}
              </div>
            )}

            <main className="app-main">
              {currentView === 'home' && (
                <HomePage
                  onNavigate={setCurrentView}
                  refreshTrigger={refreshTrigger}
                />
              )}
              {currentView === 'bathroom' && (
                <BathroomForm
                  onSuccess={(traceId) => {
                    handleSuccess(traceId);
                    setCurrentView('timeline');
                  }}
                  onError={handleError}
                />
              )}
              {currentView === 'meal' && (
                <MealForm onSuccess={handleSuccess} onError={handleError} onViewTrace={handleViewTrace} />
              )}
              {currentView === 'weight' && (
                <WeightForm onSuccess={handleSuccess} onError={handleError} />
              )}
              {currentView === 'blood_pressure' && (
                <BloodPressureForm onSuccess={handleSuccess} onError={handleError} />
              )}
              {currentView === 'water' && (
                <WaterForm onSuccess={handleSuccess} onError={handleError} />
              )}
              {currentView === 'other_inputs' && (
                <OtherInputsMenuWrapper
                  onSuccess={() => {
                    handleSuccess('other-input-logged');
                    setRefreshTrigger(prev => prev + 1);
                  }}
                  onClose={() => setCurrentView('home')}
                />
              )}
              {currentView === 'timeline' && (
                <div className="timeline-view">
                  <Timeline refreshTrigger={refreshTrigger} highlightTraceId={highlightTraceId} />
                </div>
              )}
              {currentView === 'journal' && (
                <div className="timeline-view">
                  <DailySummary refreshTrigger={refreshTrigger} />
                </div>
              )}
              {currentView === 'stats' && (
                <Dashboard />
              )}
              {currentView === 'profile' && (
                <ProfilePage onNavigate={setCurrentView} />
              )}
              {currentView === 'assistant' && (
                <HealthAssistantPage />
              )}
              {currentView === 'dashboard' && (
                <OperationalDashboard />
              )}
              {currentView === 'knowledge' && (
                <KnowledgePage onNavigate={setCurrentView} />
              )}
            </main>

            <footer className="app-footer">
              <p>Personal Health Observability Platform</p>
              <p className="footer-note">Data encrypted and secure</p>
            </footer>
            {showMoodPrompt && (
              <MoodPrompt
                onComplete={() => setShowMoodPrompt(false)}
                lastEventType={lastLoggedType}
                relatedTraceId={lastLoggedTraceId}
              />
            )}
          </div>
        </UserProvider>
      )}
    </Authenticator >
  );
}

export default App;
